import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';
import { environmentBase } from '../environments/environment.base';
import { Client } from '../models/client.model';
import { isActiveMasterClient } from '../utils/active-master-client.util';

export interface UserContextLoadResult {
  context: UserContext;
  /** HTTP status from GET /me/context (201 when user was just provisioned from legacy). */
  status: number;
}

/**
 * User context from GET /me/context
 */
export interface UserContext extends PortalPermissions {
  userId: string;
  name: string;
  email: string;
  portals: Array<{
    portalId: number;
    name: string; // "client" | "member" | "attorney"
    roles: string[];
  }>;
}

/**
 * Portal permissions from GET /me/portals/{portalKey}/permissions
 */
export interface PortalFirmAccess {
  firmGroupId: number;
  firmId: number;
  /** FirmDetail.FIRM_CODE. Routing and access still use firmId. */
  firmCode?: number | null;
  firmName?: string | null;
  firmType?: string | null;
  roles: string[];
  permissions: string[];
}

export interface PortalFirmGroupOption {
  firmGroupId: number;
  firmId: number;
  firmCode: number | null;
  firmName: string;
}

export interface PortalPermissions {
  portal: string;
  permissions: string[];
  clients: Array<{
    mCode: string;
    name: string;
    status?: string;
    active?: boolean;
    subClients: Array<{
      cCode: string;
      name: string;
    }>;
  }>;
  /** Attorney login only. Client context leaves this empty. */
  firms?: PortalFirmAccess[];
}

/** Wrapped shape stored under lr_portal_permissions (TTL metadata). */
interface PersistedPortalPermissions {
  data: PortalPermissions;
  /** Unix timestamp (ms) when the cache must be discarded. */
  expiresAt: number;
}

/**
 * Service for managing user context and portal initialization
 * Handles loading user context and portal-specific permissions
 * Persists data to localStorage to avoid API calls on page refresh.
 * Portal permissions are stored with a TTL so backend changes are picked up after refresh or re-login.
 */
@Injectable({
  providedIn: 'root'
})
export class ContextService {
  private http = inject(HttpClient);
  private readonly apiUrl = environmentBase.apiUrl;

  /** How long cached portal permissions remain valid in localStorage. */
  private readonly PERMISSIONS_TTL_MS = 60 * 60 * 1000;

  // Storage keys for localStorage
  private readonly STORAGE_KEY_CONTEXT = 'lr_user_context';
  private readonly STORAGE_KEY_ACTIVE_PORTAL = 'lr_active_portal';
  private readonly STORAGE_KEY_PERMISSIONS = 'lr_portal_permissions';
  private readonly STORAGE_KEY_SELECTED_CLIENT = 'lr_selected_client';
  private readonly STORAGE_KEY_SELECTED_FIRM = 'lr_selected_firm';
  private readonly STORAGE_KEY_PENDING_NOTIFICATION_SETUP = 'lr_pending_notification_setup';

  // BehaviorSubjects for state management
  private contextSubject = new BehaviorSubject<UserContext | null>(null);
  private activePortalSubject = new BehaviorSubject<string | null>(null);
  private permissionsSubject = new BehaviorSubject<PortalPermissions | null>(null);
  private selectedClientSubject = new BehaviorSubject<Client | null>(null);
  private selectedFirmSubject = new BehaviorSubject<PortalFirmAccess | null>(null);
  private contextReadySubject = new BehaviorSubject<boolean>(false);
  private pendingNotificationSetupSubject = new BehaviorSubject<boolean>(false);
  /** Set when this tab received 401 / dead Auth0 session so guards do not treat cache as "no groups". */
  private sessionRejected = false;

  constructor() {
    // Load persisted data from localStorage on service initialization
    // This only happens on page refresh, not on new login sessions
    // New sessions will load data from API in the callback component
    this.loadFromStorage();
  }

  // Public observables
  context$: Observable<UserContext | null> = this.contextSubject.asObservable();
  activePortal$: Observable<string | null> = this.activePortalSubject.asObservable();
  permissions$: Observable<PortalPermissions | null> = this.permissionsSubject.asObservable();
  selectedClient$: Observable<Client | null> = this.selectedClientSubject.asObservable();
  selectedFirm$: Observable<PortalFirmAccess | null> = this.selectedFirmSubject.asObservable();

  // contextReady$ is true only after: context loaded + permissions loaded + portal selected
  contextReady$: Observable<boolean> = combineLatest([
    this.contextSubject.asObservable(),
    this.activePortalSubject.asObservable(),
    this.permissionsSubject.asObservable()
  ]).pipe(
    map(([context, activePortal, permissions]) => {
      if (!context || !activePortal || !permissions) {
        return false;
      }
      const hasPermissions = (permissions.permissions?.length ?? 0) > 0;
      const hasClients = (permissions.clients?.length ?? 0) > 0;
      const hasFirms = (permissions.firms?.length ?? 0) > 0;
      return hasPermissions || hasClients || hasFirms;
    })
  );

  /**
   * Load user context from GET /me/context
   * Also persists to localStorage
   */
  loadUserContext(portalKey: string): Observable<UserContextLoadResult> {
    return this.http
      .get<UserContext>(`${this.apiUrl}/me/context/portals/${portalKey}`, { observe: 'response' })
      .pipe(
        map(res => {
          const context = res.body as UserContext;
          this.contextSubject.next(context);
          this.saveToStorage('context', context);
          const permissions: PortalPermissions = {
            portal: context.portal,
            permissions: context.permissions,
            clients: context.clients,
            firms: context.firms ?? []
          };
          // Restore selectedClient from storage before emitting permissions (avoids app overwriting with first client)
          try {
            const selectedClientStr = localStorage.getItem(this.STORAGE_KEY_SELECTED_CLIENT);
            if (selectedClientStr) {
              const selectedClientMCode = JSON.parse(selectedClientStr) as string;
              const clients = this.mapPermissionsToClients(permissions);
              const client = clients.find(c => c.mCode === selectedClientMCode);
              if (client) {
                this.selectedClientSubject.next(client);
              }
            }
          } catch {
            // Ignore invalid stored selectedClient
          }
          this.applyStoredFirm(permissions);
          this.permissionsSubject.next(permissions);
          this.saveToStorage('permissions', permissions);
          if (res.status === 201) {
            this.setPendingNotificationSetup(true);
          }
          this.sessionRejected = false;
          return { context, status: res.status };
        })
      );
  }

  /**
   * Set the active portal
   * This should be called after selecting a portal (either manually or auto-selected)
   * Also persists to localStorage
   */
  setActivePortal(portalKey: string): void {
    this.activePortalSubject.next(portalKey);
    this.saveToStorage('activePortal', portalKey);
  }

  /**
   * Get the current user context
   */
  getContext(): UserContext | null {
    return this.contextSubject.value;
  }

  /**
   * Get the current active portal
   */
  getActivePortal(): string | null {
    return this.activePortalSubject.value;
  }

  /**
   * Get the current portal permissions
   */
  getPermissions(): PortalPermissions | null {
    return this.permissionsSubject.value;
  }

  get isSessionRejected(): boolean {
    return this.sessionRejected;
  }

  /** Drop cached context after 401 so hasGroup does not show "no groups". */
  rejectSession(): void {
    this.sessionRejected = true;
    this.contextSubject.next(null);
    this.permissionsSubject.next(null);
    this.selectedClientSubject.next(null);
    this.selectedFirmSubject.next(null);
    this.contextReadySubject.next(false);
    this.clearPendingNotificationSetup();
    try {
      localStorage.removeItem(this.STORAGE_KEY_CONTEXT);
      localStorage.removeItem(this.STORAGE_KEY_PERMISSIONS);
      localStorage.removeItem(this.STORAGE_KEY_SELECTED_CLIENT);
      localStorage.removeItem(this.STORAGE_KEY_SELECTED_FIRM);
    } catch {
      // Ignore localStorage errors
    }
  }

  /**
   * Get clients from the current portal permissions
   */
  getClients(): Client[] {
    const permissions = this.permissionsSubject.value;
    return this.mapPermissionsToClients(permissions);
  }

  /**
   * Map permissions object to Client array (used when permissions not yet in subject)
   */
  private mapPermissionsToClients(permissions: PortalPermissions | null): Client[] {
    if (!permissions || !permissions.clients) {
      return [];
    }
    return permissions.clients
      .filter(client => isActiveMasterClient(client))
      .map(client => ({
        mCode: client.mCode,
        name: client.name,
        status: client.status ?? (client as { Status?: string }).Status,
        subClients: Array.isArray(client.subClients) ? client.subClients.map(subClient => ({
          cCode: subClient.cCode,
          name: subClient.name
        })) : []
      }));
  }

  /**
   * Get roles for the active portal from context
   */
  getRoles(): string[] {
    const context = this.contextSubject.value;
    const activePortal = this.activePortalSubject.value;
    if (!context || !activePortal) {
      return [];
    }
    const portal = context.portals.find(p => p.name?.toLowerCase() === activePortal?.toLowerCase());
    return portal?.roles || [];
  }

  /**
   * Get portalId for the active portal from context
   */
  getActivePortalId(): number | null {
    const context = this.contextSubject.value;
    const activePortal = this.activePortalSubject.value;
    if (!context || !activePortal) {
      return null;
    }
    const portal = context.portals.find(p => p.name?.toLowerCase() === activePortal?.toLowerCase());
    return portal?.portalId || null;
  }

  /**
   * Set the selected client by mCode
   */
  setSelectedClient(mCode: string): void {
    const clients = this.getClients();
    const client = clients.find(c => c.mCode === mCode);

    // Only emit if the client actually changed
    const currentClient = this.selectedClientSubject.value;
    if (currentClient?.mCode !== client?.mCode) {
      this.selectedClientSubject.next(client || null);
      this.saveToStorage('selectedClient', client?.mCode || null);
    }
  }

  /**
   * Get the currently selected client
   */
  getSelectedClient(): Client | null {
    return this.selectedClientSubject.value;
  }

  /**
   * Point the permission list at one firm. Client permissions are not changed here.
   */
  setSelectedFirm(firmId: number): void {
    const permissions = this.permissionsSubject.value;
    const firm = permissions?.firms?.find(item => item.firmId === firmId);
    if (!permissions || !firm) {
      return;
    }
    if (this.selectedFirmSubject.value?.firmId === firm.firmId
      && samePermissionCodes(permissions.permissions, firm.permissions)) {
      return;
    }
    const next: PortalPermissions = {
      ...permissions,
      permissions: [...firm.permissions]
    };
    this.selectedFirmSubject.next(firm);
    this.permissionsSubject.next(next);
    this.saveToStorage('permissions', next);
    this.saveToStorage('selectedFirm', firm.firmId);
  }

  getSelectedFirm(): PortalFirmAccess | null {
    return this.selectedFirmSubject.value;
  }

  /**
   * One option per firm group. The parent entry is used when the user has it.
   * An office-only grant uses the first office in that group.
   */
  getFirmGroups(): PortalFirmGroupOption[] {
    const firms = this.permissionsSubject.value?.firms ?? [];
    const byGroup = new Map<number, PortalFirmAccess>();
    for (const firm of firms) {
      const current = byGroup.get(firm.firmGroupId);
      if (!current || firm.firmId === firm.firmGroupId) {
        byGroup.set(firm.firmGroupId, firm);
      }
    }

    return [...byGroup.values()]
      .map(firm => ({
        firmGroupId: firm.firmGroupId,
        firmId: firm.firmId,
        firmCode: firm.firmCode ?? null,
        firmName: firm.firmName?.trim() || `Firm ${firm.firmGroupId}`
      }))
      .sort((a, b) => a.firmName.localeCompare(b.firmName));
  }

  /**
   * True when this login still needs the notification opt-in (legacy 201 provision, or no saved prefs).
   * Cleared after notification setup save/skip or logout.
   */
  isPendingNotificationSetup(): boolean {
    return this.pendingNotificationSetupSubject.value;
  }

  setPendingNotificationSetup(pending: boolean): void {
    this.pendingNotificationSetupSubject.next(pending);
    try {
      if (pending) {
        sessionStorage.setItem(this.STORAGE_KEY_PENDING_NOTIFICATION_SETUP, 'true');
      } else {
        sessionStorage.removeItem(this.STORAGE_KEY_PENDING_NOTIFICATION_SETUP);
      }
    } catch {
      // Ignore sessionStorage errors
    }
  }

  clearPendingNotificationSetup(): void {
    this.setPendingNotificationSetup(false);
  }

  /**
   * Reset all state (useful for logout)
   * Also clears localStorage
   */
  reset(): void {
    this.sessionRejected = false;
    this.contextSubject.next(null);
    this.activePortalSubject.next(null);
    this.permissionsSubject.next(null);
    this.selectedClientSubject.next(null);
    this.selectedFirmSubject.next(null);
    this.contextReadySubject.next(false);
    this.clearPendingNotificationSetup();
    this.clearStorage();
  }

  /**
   * Save data to localStorage
   */
  private applyStoredFirm(permissions: PortalPermissions): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY_SELECTED_FIRM);
      if (!stored) {
        return;
      }
      const firmId = JSON.parse(stored) as number;
      const firm = permissions.firms?.find(item => item.firmId === firmId);
      if (!firm) {
        return;
      }
      permissions.permissions = [...firm.permissions];
      this.selectedFirmSubject.next(firm);
    } catch {
      // Ignore invalid stored firm
    }
  }

  private saveToStorage(key: 'context' | 'activePortal' | 'permissions' | 'selectedClient' | 'selectedFirm', value: any): void {
    try {
      const storageKey = key === 'context'
        ? this.STORAGE_KEY_CONTEXT
        : key === 'activePortal'
        ? this.STORAGE_KEY_ACTIVE_PORTAL
        : key === 'permissions'
        ? this.STORAGE_KEY_PERMISSIONS
        : key === 'selectedFirm'
        ? this.STORAGE_KEY_SELECTED_FIRM
        : this.STORAGE_KEY_SELECTED_CLIENT;

      if (key === 'permissions') {
        const payload: PersistedPortalPermissions = {
          data: value as PortalPermissions,
          expiresAt: Date.now() + this.PERMISSIONS_TTL_MS
        };
        localStorage.setItem(storageKey, JSON.stringify(payload));
      } else {
        localStorage.setItem(storageKey, JSON.stringify(value));
      }
    } catch (error) {
      console.warn('Failed to save to localStorage:', error);
    }
  }

  /**
   * Reads persisted portal permissions; returns null if missing, malformed, legacy (no TTL), or expired.
   */
  private readPermissionsFromStorage(raw: string): PortalPermissions | null {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (!parsed || typeof parsed !== 'object') {
        return null;
      }
      const obj = parsed as Record<string, unknown>;
      if (
        typeof obj['expiresAt'] === 'number' &&
        obj['data'] &&
        typeof obj['data'] === 'object'
      ) {
        if (Date.now() > obj['expiresAt']) {
          return null;
        }
        return obj['data'] as PortalPermissions;
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Load data from localStorage on service initialization.
   * Validates that cached data belongs to the current Auth0 user before using it.
   * Clears cache if user mismatch or data from before auth-sub validation (legacy).
   */
  private loadFromStorage(): void {
    try {
      // Load context
      const contextStr = localStorage.getItem(this.STORAGE_KEY_CONTEXT);
      if (contextStr) {
        const context = JSON.parse(contextStr) as UserContext;
        this.contextSubject.next(context);
        console.log('ContextService: Loaded context from localStorage', context);
      }

      // Load active portal
      const portalStr = localStorage.getItem(this.STORAGE_KEY_ACTIVE_PORTAL);
      if (portalStr) {
        const portal = JSON.parse(portalStr) as string;
        this.activePortalSubject.next(portal);
        console.log('ContextService: Loaded active portal from localStorage', portal);
      }

      // Load permissions (TTL-wrapped; stale or legacy entries are dropped)
      const permissionsStr = localStorage.getItem(this.STORAGE_KEY_PERMISSIONS);
      if (permissionsStr) {
        const permissions = this.readPermissionsFromStorage(permissionsStr);
        if (permissions) {
          this.permissionsSubject.next(permissions);
          console.log('ContextService: Loaded permissions from localStorage', permissions);
        } else {
          localStorage.removeItem(this.STORAGE_KEY_PERMISSIONS);
          console.log('ContextService: Portal permissions cache expired or invalid; removed from localStorage');
        }
      }

      // Load selected client
      const selectedClientStr = localStorage.getItem(this.STORAGE_KEY_SELECTED_CLIENT);
      if (selectedClientStr) {
        const selectedClientMCode = JSON.parse(selectedClientStr) as string;
        // Try to find the client in the current clients list
        const clients = this.getClients();
        const client = clients.find(c => c.mCode === selectedClientMCode);
        if (client) {
          this.selectedClientSubject.next(client);
          console.log('ContextService: Loaded selected client from localStorage', client);
        }
      }

      const selectedFirmStr = localStorage.getItem(this.STORAGE_KEY_SELECTED_FIRM);
      if (selectedFirmStr) {
        const firmId = JSON.parse(selectedFirmStr) as number;
        const firm = this.permissionsSubject.value?.firms?.find(item => item.firmId === firmId);
        if (firm) {
          this.selectedFirmSubject.next(firm);
        }
      }

      const pendingSetup = sessionStorage.getItem(this.STORAGE_KEY_PENDING_NOTIFICATION_SETUP) === 'true';
      this.pendingNotificationSetupSubject.next(pendingSetup);
    } catch (error) {
      console.warn('Failed to load from localStorage:', error);
      // Clear corrupted data
      this.clearStorage();
    }
  }

  /**
   * Clear all stored data from localStorage
   */
  private clearStorage(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY_CONTEXT);
      localStorage.removeItem(this.STORAGE_KEY_ACTIVE_PORTAL);
      localStorage.removeItem(this.STORAGE_KEY_PERMISSIONS);
      localStorage.removeItem(this.STORAGE_KEY_SELECTED_CLIENT);
      localStorage.removeItem(this.STORAGE_KEY_SELECTED_FIRM);
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }
  }
}

function samePermissionCodes(current: string[] | undefined, next: string[]): boolean {
  const left = current ?? [];
  return left.length === next.length && left.every((code, index) => code === next[index]);
}
